import { types, flow, getRoot } from 'mobx-state-tree'
import _ from 'lodash'

import { API } from '../api'

const { model, maybeNull, number, string, array } = types

const ValueModel = model({
  percent: maybeNull(number),
  dollar: maybeNull(number),
})

const AssetModel = model({
  name: maybeNull(string),
  oldValue: maybeNull(ValueModel),
  newValue: maybeNull(ValueModel),
})

const InvMixModel = model({
  name: maybeNull(string),
  assets: array(AssetModel),
})

const Breakdown = model({
  assetClassBreakdown: array(InvMixModel),
  investmentCategory: array(InvMixModel),
  investmentHoldings: array(InvMixModel),
})

const Asset = model('Asset', {
  name: maybeNull(string),
  newValue: maybeNull(ValueModel),
  oldValue: maybeNull(ValueModel),
  ticker: maybeNull(string),
})

const AssetClassBreakdown = model('AssetClassBreakdown', {
  assets: array(Asset),
  name: maybeNull(string),
  newDollars: maybeNull(number),
  newPercent: maybeNull(number),
  oldDollars: maybeNull(number),
  oldPercent: maybeNull(number),
  type: maybeNull(number),
})

const InvestmentCategory = model('InvestmentCategory', {
  assets: array(Asset),
  name: maybeNull(string),
  newDollars: maybeNull(number),
  newPercent: maybeNull(number),
  oldDollars: maybeNull(number),
  oldPercent: maybeNull(number),
  type: maybeNull(number),
})

const InvestmentHoldings = model('InvestmentHoldings', {
  assets: array(Asset),
  name: maybeNull(string),
  newDollars: maybeNull(number),
  newPercent: maybeNull(number),
  oldDollars: maybeNull(number),
  oldPercent: maybeNull(number),
  type: maybeNull(number),
})

const NewBreakdown = model('NewBreakdown', {
  assetClassBreakdown: array(AssetClassBreakdown),
  investmentCategory: array(InvestmentCategory),
  investmentHoldings: array(InvestmentHoldings),
})

// Normalize missing collections, remove assets with no old or new value, and
// treat zero-dollar/zero-percent values as absent.
function cleanBreakdownData(breakdownData) {
  function cleanCategory(category) {
    if (!category) {
      return []
    }

    return category.map(cleanInvestmentMix)
  }

  function cleanInvestmentMix(investmentMix) {
    const { name, assets } = investmentMix
    return { name, assets: cleanAssets(assets) }
  }

  function cleanAssets(assets) {
    if (!assets) {
      return []
    }

    return assets
      .map(cleanAsset)
      .filter(asset => !(asset.oldValue === null && asset.newValue === null))
  }

  function cleanAsset(asset) {
    const { name, oldValue, newValue } = asset

    return {
      name,
      oldValue:
        oldValue !== null && oldValue.dollar === 0 && oldValue.percent === 0 ? null : oldValue,
      newValue:
        newValue !== null && newValue.dollar === 0 && newValue.percent === 0 ? null : newValue,
    }
  }

  return _.mapValues(breakdownData, cleanCategory)
}

// Preserve tickers for display and group types for matching holdings to asset
// classes, without changing the existing `allBreakdown` shape.
function cleanBreakdownDataWithTicker(breakdownData) {
  function cleanCategory(category) {
    return category ? category.map(cleanInvestmentMix) : []
  }

  function cleanInvestmentMix(investmentMix) {
    const { name, type, assets } = investmentMix
    return { name, type, assets: cleanAssets(assets) }
  }

  function cleanAssets(assets) {
    return assets
      ? assets
          .map(cleanAsset)
          .filter(asset => !(asset.oldValue === null && asset.newValue === null))
      : []
  }

  function cleanAsset(asset) {
    const { name, ticker, oldValue, newValue } = asset

    return {
      name,
      ticker: ticker ?? null,
      oldValue:
        oldValue !== null && oldValue.dollar === 0 && oldValue.percent === 0 ? null : oldValue,
      newValue:
        newValue !== null && newValue.dollar === 0 && newValue.percent === 0 ? null : newValue,
    }
  }

  return _.mapValues(breakdownData, cleanCategory)
}

const BreakdownStore = model({
  breakdown: maybeNull(Breakdown),
  allBreakdownData: types.map(NewBreakdown),
})
  .views(self => ({
    get allBreakdown() {
      return [...self.allBreakdownData].reduce((acc, [key, value]) => {
        const cleanBreakDown = cleanBreakdownData(value)

        acc.push({
          name: key,
          assetClassBreakdown: cleanBreakDown.assetClassBreakdown,
          investmentCategory: cleanBreakDown.investmentCategory,
          investmentHoldings: cleanBreakDown.investmentHoldings,
        })

        return acc
      }, [])
    },

    // Preserves asset tickers for the advice investment display.
    get allBreakdownDetailed() {
      return [...self.allBreakdownData].reduce((acc, [key, value]) => {
        const cleanBreakDown = cleanBreakdownDataWithTicker(value)

        acc.push({
          name: key,
          assetClassBreakdown: cleanBreakDown.assetClassBreakdown,
          investmentCategory: cleanBreakDown.investmentCategory,
          investmentHoldings: cleanBreakDown.investmentHoldings,
        })

        return acc
      }, [])
    },
  }))
  .actions(self => ({
    afterAttach: flow(function* () {
      yield self.fetchBreakdown()
    }),

    getBreakdown: flow(function* (caseId) {
      const breakdown = yield API.get('breakdown/institutional', {
        params: { caseId },
      })
      self.breakdown = cleanBreakdownData(breakdown.data)
    }),

    getAllBreakdown: flow(function* (caseId) {
      const allBreakdown = yield API.get('breakdown/all', {
        params: { caseId },
      })
      self.allBreakdownData = allBreakdown.data || {}
      return allBreakdown.data
    }),

    // Clear existing data before a fetch so a failure cannot leave callers
    // using a breakdown from the previous case.
    clearAllBreakdown() {
      self.allBreakdownData = {}
    },

    // Keep the current breakdown while the new one loads so consumers never see an
    // empty breakdown. Clear it if the request fails so stale data is not retained.
    refreshAllBreakdown: flow(function* () {
      const { modifiedCase, recommendedCase } = getRoot(self)
      const caseId = (modifiedCase || recommendedCase)?.caseId

      if (caseId == null) {
        self.allBreakdownData = {}
        return
      }

      try {
        yield self.getAllBreakdown(caseId)
      } catch (err) {
        self.allBreakdownData = {}
        throw err
      }
    }),
  }))

export default BreakdownStore
